// RouteRecords.jsx
import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { Calendar, CheckCircle, Clock, AlertTriangle, ChevronLeft, ChevronRight, User, MapPin } from 'lucide-react';
import { AuthContext } from "../../context/AuthContext";
import { 
  fetchClientesPorAdministrativo, 
  fetchClientesPorInspector, 
  fetchTiendasPorCampoCliente 
} from "../admin/firestoreService";
import './styles/routeRecords.scss';

const RouteRecords = ({ routeId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const recordsPerPage = 10;

  const { currentService, adminData } = useContext(AuthContext);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [selectedServicio, setSelectedServicio] = useState('');

  useEffect(() => {
    if (routeId && currentService) {
      fetchRouteRecords();
    }
  }, [routeId, currentPage, currentService]);

  useEffect(() => {
    // Cargar clientes y servicios filtrados por permisos usando firestoreService
    const fetchClientesServicios = async () => {
      if (!currentService?.id || !adminData?.id) return;
      
      try {
        let clientesList = [];
        
        // Usar las funciones de firestoreService según el tipo de usuario
        if (adminData.tipoUsuario?.includes('administrativo')) {
          clientesList = await fetchClientesPorAdministrativo(adminData.id, currentService);
        } else if (adminData.tipoUsuario?.includes('inspector')) {
          clientesList = await fetchClientesPorInspector(adminData.id, currentService);
        } else if (adminData.tipoUsuario?.includes('administrador')) {
          // Si es administrador, cargar todos los clientes
          const { fetchClientesYTiendas } = await import("../admin/firestoreService");
          const [clientesData] = await fetchClientesYTiendas(currentService);
          clientesList = clientesData;
        }
        
        setClientes(clientesList);
        
        // Cargar servicios basados en los clientes permitidos
        if (clientesList.length > 0) {
          const clientesIds = clientesList.map(c => c.id);
          const serviciosList = await fetchTiendasPorCampoCliente(clientesIds, currentService);
          setServicios(serviciosList);
        } else {
          setServicios([]);
        }
      } catch (error) {
        console.error('Error loading clientes and servicios:', error);
        setClientes([]);
        setServicios([]);
      }
    };
    
    fetchClientesServicios();
  }, [currentService, adminData]);

  const fetchRouteRecords = async () => {
    try {
      setLoading(true);
      
      // Consultar los registros de esta ruta en la subcolección del servicio actual
      const recordsRef = collection(db, 'servicios', currentService.id, 'registrosRuta');
      let q = query(recordsRef);
      if (routeId) {
        q = query(q, where('rutaId', '==', routeId));
      }
      if (selectedCliente) {
        q = query(q, where('clienteId', '==', selectedCliente));
      }
      if (selectedServicio) {
        q = query(q, where('servicioId', '==', selectedServicio));
      }
      q = query(q, orderBy('fechaProgramada', 'desc'), limit(recordsPerPage));
      
      const querySnapshot = await getDocs(q);
      
      // Convertir a formato utilizable
      const recordsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convertir timestamps de Firestore a Date si existen
        scheduledTime: doc.data().fechaProgramada?.toDate ? doc.data().fechaProgramada.toDate() : doc.data().fechaProgramada,
        startTime: doc.data().horaInicio?.toDate ? doc.data().horaInicio.toDate() : doc.data().horaInicio,
        endTime: doc.data().horaFin?.toDate ? doc.data().horaFin.toDate() : doc.data().horaFin
      }));
      
      setRecords(recordsList);
      
      // Calcular el número total de páginas basado en el tamaño de la consulta
      setTotalPages(Math.ceil(querySnapshot.size / recordsPerPage));
      if (totalPages === 0) setTotalPages(1); // Al menos una página incluso si no hay resultados
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching route records:', err);
      setError('Error al cargar los registros de rondas');
      setLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
  };

  const handleCloseDetails = () => {
    setSelectedRecord(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'completada':
        return (
          <span className="status-badge completed">
            <CheckCircle size={12} className="icon" />
            Completada
          </span>
        );
      case 'in-progress':
      case 'en-progreso':
        return (
          <span className="status-badge in-progress">
            <Clock size={12} className="icon" />
            En Progreso
          </span>
        );
      case 'missed':
      case 'perdida':
        return (
          <span className="status-badge missed">
            <AlertTriangle size={12} className="icon" />
            Perdida
          </span>
        );
      default:
        return (
          <span className="status-badge default">
            {status}
          </span>
        );
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString();
  };

  return (
    <div className="route-records">
      <h3 className="records-title">Historial de Rondas</h3>

      {/* Filtros de cliente y servicio */}
      <div className="filters-container" style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div className="filter-group">
          <label>Cliente:</label>
          <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)}>
            <option value="">Todos los clientes</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>{cliente.nombre}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Servicio:</label>
          <select value={selectedServicio} onChange={e => setSelectedServicio(e.target.value)}>
            <option value="">Todos los servicios</option>
            {servicios.map(servicio => (
              <option key={servicio.id} value={servicio.id}>{servicio.numTienda || servicio.nombre || servicio.descripcion || servicio.id}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading-message">Cargando registros...</div>
      ) : selectedRecord ? (
        <div className="record-details">
          <div className="details-header">
            <h4 className="header-title">
              <Calendar size={18} className="icon" />
              Detalles de Ronda - {formatDate(selectedRecord.scheduledTime)}
            </h4>
            <button
              onClick={handleCloseDetails}
              className="back-button"
            >
              <ChevronLeft size={20} className="icon" />
              Volver
            </button>
          </div>
          
          <div className="details-content">
            <div className="details-grid">
              <div className="detail-item">
                <div className="label">Estado</div>
                <div className="value">{getStatusBadge(selectedRecord.status || selectedRecord.estado)}</div>
              </div>
              
              <div className="detail-item">
                <div className="label">Usuario</div>
                <div className="value with-icon">
                  <User size={16} className="icon" />
                  {selectedRecord.nombreVigilante || selectedRecord.guardName || 'Sin asignar'}
                </div>
              </div>
              
              <div className="detail-item">
                <div className="label">Programada para</div>
                <div className="value">{formatDateTime(selectedRecord.scheduledTime)}</div>
              </div>
              
              <div className="detail-item">
                <div className="label">Duración</div>
                <div className="value">
                  {selectedRecord.startTime && selectedRecord.endTime ? (
                    `${Math.round((selectedRecord.endTime - selectedRecord.startTime) / 60000)} minutos`
                  ) : 'N/A'}
                </div>
              </div>
              
              <div className="detail-item">
                <div className="label">Hora de inicio</div>
                <div className="value">{formatTime(selectedRecord.startTime)}</div>
              </div>
              
              <div className="detail-item">
                <div className="label">Hora de finalización</div>
                <div className="value">{formatTime(selectedRecord.endTime)}</div>
              </div>
            </div>
            
            <h5 className="checkpoints-title">Puntos Verificados</h5>
            
            {(selectedRecord.puntosRegistro || selectedRecord.checkpointRecords)?.length > 0 ? (
              <div className="checkpoints-list">
                {(selectedRecord.puntosRegistro || selectedRecord.checkpointRecords).map((checkpoint, index) => (
                  <div 
                    key={checkpoint.puntoId || checkpoint.checkpointId || index}
                    className="checkpoint-item"
                  >
                    <div className="checkpoint-content">
                      <div className="checkpoint-info">
                        <div className="checkpoint-name">Punto {index + 1}</div>
                        <div className="checkpoint-location">
                          <MapPin size={14} className="icon" />
                          {checkpoint.puntoId || checkpoint.checkpointId}
                        </div>
                      </div>
                      <div className={(checkpoint.estado || checkpoint.status) === 'checked' ? 'checkpoint-status checked' : 'checkpoint-status not-checked'}>
                        {(checkpoint.estado || checkpoint.status) === 'checked' ? (
                          <>
                            <CheckCircle size={16} className="icon" />
                            {formatTime(checkpoint.timestamp)}
                          </>
                        ) : (
                          'No verificado'
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-checkpoints">No hay registros de puntos de control</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="records-table-container">
            <table>
              <thead>
                <tr>
                  <th>Fecha/Hora</th>
                  <th>Usuario</th>
                  <th>Estado</th>
                  <th>Duración</th>
                  <th className="actions-column">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-message">
                      No hay registros de rondas para esta ruta
                    </td>
                  </tr>
                ) : (
                  records.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div className="date-cell">
                          <Calendar size={16} className="icon" />
                          <div className="date-info">
                            <div className="date">{formatDate(record.scheduledTime)}</div>
                            <div className="time">{formatTime(record.scheduledTime)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="guard-cell">
                          <User size={16} className="icon" />
                          <span className="guard-name">{record.nombreVigilante || record.guardName || 'Sin asignar'}</span>
                        </div>
                      </td>
                      <td>{getStatusBadge(record.status || record.estado)}</td>
                      <td className="duration-cell">
                        {record.startTime && record.endTime ? (
                          `${Math.round((record.endTime - record.startTime) / 60000)} min`
                        ) : 'N/A'}
                      </td>
                      <td>
                        <div style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => handleViewDetails(record)}
                            className="details-button"
                          >
                            Detalles
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="pagination-container">
            <div className="page-info">
              Página {currentPage} de {totalPages}
            </div>
            <div className="page-buttons">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className={currentPage <= 1 ? 'page-button disabled' : 'page-button active'}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className={currentPage >= totalPages ? 'page-button disabled' : 'page-button active'}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RouteRecords;